import { useCallback, useRef, useState } from 'react';

export const MAX_RECORD_SECONDS = 60;

function pickMimeType() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  if (window.MediaRecorder?.isTypeSupported) {
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
  }
  return '';
}

// onComplete(blob, mimeType) fires when recording stops (manual or 60s cap).
export function useRecorder({ onComplete, onError }) {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  }, []);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError('Tu navegador no permite grabar audio. Usa Safari o Chrome actualizado.');
      return false;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        onError('No hay permiso para usar el micrófono. Actívalo en la configuración de tu navegador e intenta de nuevo.');
      } else if (name === 'NotFoundError') {
        onError('No se encontró un micrófono en este dispositivo.');
      } else {
        onError('No se pudo acceder al micrófono. Intenta de nuevo.');
      }
      return false;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      cleanup();
      if (blob.size < 1000) {
        onError('La grabación quedó vacía. Intenta de nuevo hablando claro.');
      } else {
        onComplete(blob, type);
      }
    };
    recorder.onerror = () => {
      cleanup();
      onError('Ocurrió un error al grabar. Intenta de nuevo.');
    };

    recorder.start(250);
    setIsRecording(true);
    setSeconds(0);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setSeconds(elapsed);
      if (elapsed >= MAX_RECORD_SECONDS) stop();
    }, 250);
    return true;
  }, [cleanup, onComplete, onError, stop]);

  return { isRecording, seconds, start, stop };
}
